import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { DocumentDashboardDateField } from '@risksmart-app/domain/src/types/consts/dashboard-date-fields';
import type { DocumentRegisterResponse } from '@risksmart-app/trpc/src/types';
import type {
  Document_Bool_Exp,
  GetDocumentsQuery,
  GetDocumentsQueryVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetDocumentsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import _ from 'lodash';
import { createQueryHook, type StrictMappedGraphQLSelection } from 'src/utils';

import { DOCUMENT_DATE_PREDICATES } from '@/hooks/queries/dashboardDatePredicates';
import {
  buildDashboardWhere,
  type DashboardFilterArgs,
  departmentsWhereOnEntity,
  tagsWhereOnEntity,
  toTrpcDashboardFilterInput,
} from '@/hooks/queries/dashboardFilterArgs';

/**
 * Maps TRPC document data to match the GraphQL query structure
 */
export function mapTrpcDocumentsToGraphQL(
  trpcData: DocumentRegisterResponse[]
): StrictMappedGraphQLSelection<GetDocumentsQuery> {
  const registerData = trpcData[0];
  if (!registerData) {
    return { document: [], assessment_result_parent: [] };
  }

  const documentWithLatestPublishedVersion = registerData.document.map(
    (document) => {
      const previouslyPublishedVersions = document?.documentFiles.filter(
        (documentFile) => !!documentFile.PublishedDate
      );

      const latestPublishedVersion = _.orderBy(
        previouslyPublishedVersions,
        ['PublishedDate'],
        ['desc']
      )[0];

      const latestDocumentFile = _.orderBy(
        document?.documentFiles,
        ['CreatedAtTimestamp'],
        ['desc']
      )[0];

      return {
        ...document,
        documentFiles: [latestDocumentFile],
        latestPublishedVersion: [
          {
            PublishedDate: latestPublishedVersion?.PublishedDate,
          },
        ],
      };
    }
  );

  return {
    document: documentWithLatestPublishedVersion,
    assessment_result_parent: registerData.assessment_result_parent,
  };
}

const usePolicyRegisterGraphqlVariables = (args: UseGetPolicyRegisterArgs) => {
  const { user } = useRisksmartUser();

  // Explicit type arguments: document has no `base` (its own predicate is
  // `undefined`), so there is nothing to anchor `TWhere` for inference — see
  // the obligation hook's identical comment. The third argument
  // (`TDateFragment`) is ALSO required here: document is the one entity whose
  // date map returns more than a bare `where` (`filesWhere`), and supplying
  // only the first two type arguments suppresses inference for the defaulted
  // `TDateFragment` generic, which would silently drop `filesWhere` from
  // `dateVariables`'s type.
  const { where, dateVariables } = buildDashboardWhere<
    DashboardFilterArgs<DocumentDashboardDateField>,
    Document_Bool_Exp,
    Partial<GetDocumentsQueryVariables>
  >({
    filters: args,
    userId: user?.userId,
    datePredicates: DOCUMENT_DATE_PREDICATES,
    tagsWhere: tagsWhereOnEntity,
    departmentsWhere: departmentsWhereOnEntity,
    ownershipWhere: (conditions) => conditions.documentFilterConditions,
  });

  return {
    includeAssessmentResultsHistory: true,
    ...dateVariables,
    ...(where ? { where } : {}),
  };
};

export type UseGetPolicyRegisterArgs =
  DashboardFilterArgs<DocumentDashboardDateField>;

export const useGetPolicyRegister = createQueryHook<
  UseGetPolicyRegisterArgs,
  DocumentRegisterResponse[],
  GetDocumentsQuery
>({
  trpcQueryOptions: (
    trpc,
    { tagTypeIds, departmentTypeIds, dateRange, ownership }
  ) =>
    trpc.frontend.document.register.queryOptions(
      toTrpcDashboardFilterInput({
        tagTypeIds,
        departmentTypeIds,
        dateRange,
        ownership,
      })
    ),
  mapTrpcDataToGraphQL: mapTrpcDocumentsToGraphQL,
  graphqlDocument: GetDocumentsDocument,
  graphqlVariables: usePolicyRegisterGraphqlVariables,
});
